import { useRef, useEffect } from "react";
import { Billboard, Text } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import type * as THREE from "three";
import { LOBBY_TICK_HZ } from "@solblox/shared";

import { FaceBillboard } from "../voice/FaceBillboard";
import { avatarMaterialProps, resolveSkinColor } from "../lobby/skinColor";

interface EcctrlAvatarProps {
  displayName: string;
  position: [number, number, number];
  rotY: number;
  /** LiveKit identity (= Colyseus sessionId). Drives the talking face. */
  identity?: string;
  /** Server-confirmed color pick. Empty = use deterministic fallback. */
  colorHex?: string;
  /** Stable identity for deterministic fallback (pubkey preferred over sessionId). */
  colorIdentity?: string;
  /** Server-verified token holder — gold name + ✦ badge on the nameplate. */
  isHolder?: boolean;
}

interface Snapshot {
  t: number;
  x: number;
  y: number;
  z: number;
  rotY: number;
}

const TICK_INTERVAL_MS = 1000 / LOBBY_TICK_HZ;
const RENDER_DELAY_MS = TICK_INTERVAL_MS * 2;
const BUFFER_CAP = 12;
const SNAP_DIST_SQ = 25;

/**
 * Remote-player visual for the ecctrl branch. Matches the local ecctrl capsule
 * (geometry + color) so all players in the scene look like one unified system.
 *
 * Uses snapshot interpolation: every received position update is timestamped
 * and pushed into a small ring; render-time samples the ring slightly in the
 * past (one tick interval behind) so we always have a "future" snapshot to
 * interpolate toward. This is the standard FPS-netcode technique and trades a
 * small fixed latency for continuous motion that doesn't ease into late targets.
 */
export function EcctrlAvatar({
  displayName,
  position,
  rotY,
  identity,
  colorHex,
  colorIdentity,
  isHolder,
}: EcctrlAvatarProps): JSX.Element {
  const groupRef = useRef<THREE.Group>(null);
  const bufferRef = useRef<Snapshot[]>([]);

  useEffect(() => {
    const buf = bufferRef.current;
    const snap: Snapshot = {
      t: performance.now(),
      x: position[0],
      y: position[1],
      z: position[2],
      rotY,
    };
    const last = buf.length > 0 ? buf[buf.length - 1] : null;
    if (last) {
      const dx = snap.x - last.x;
      const dy = snap.y - last.y;
      const dz = snap.z - last.z;
      if (dx * dx + dy * dy + dz * dz > SNAP_DIST_SQ) {
        buf.length = 0;
      }
    }
    buf.push(snap);
    if (buf.length > BUFFER_CAP) buf.shift();
  }, [position, rotY]);

  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    const buf = bufferRef.current;
    if (buf.length === 0) return;
    if (buf.length === 1) {
      const s = buf[0]!;
      g.position.set(s.x, s.y, s.z);
      g.rotation.y = s.rotY;
      return;
    }
    const renderTime = performance.now() - RENDER_DELAY_MS;
    let a: Snapshot = buf[0]!;
    let b: Snapshot = buf[buf.length - 1]!;
    for (let i = 0; i < buf.length - 1; i++) {
      const lo = buf[i]!;
      const hi = buf[i + 1]!;
      if (lo.t <= renderTime && hi.t >= renderTime) {
        a = lo;
        b = hi;
        break;
      }
    }
    const span = b.t - a.t;
    const tNorm = span > 0 ? Math.max(0, Math.min(1, (renderTime - a.t) / span)) : 1;
    g.position.set(
      a.x + (b.x - a.x) * tNorm,
      a.y + (b.y - a.y) * tNorm,
      a.z + (b.z - a.z) * tNorm,
    );
    g.rotation.y = a.rotY + wrapAngle(b.rotY - a.rotY) * tNorm;
  });

  return (
    <group ref={groupRef} position={position} rotation={[0, rotY, 0]}>
      <PlayerAvatarBody
        colorHex={colorHex}
        colorIdentity={colorIdentity ?? identity}
        displayName={displayName}
        identity={identity}
        isHolder={isHolder}
      />
    </group>
  );
}

interface PlayerAvatarBodyProps {
  /** Server-confirmed color pick. Empty = deterministic fallback. */
  colorHex?: string;
  /** Stable identity for the deterministic color fallback (pubkey preferred). */
  colorIdentity?: string;
  displayName?: string;
  /** LiveKit identity (= Colyseus sessionId). Drives the talking face. */
  identity?: string;
  /** Show the floating name label. Off for the king (it carries a 👑 label). */
  showName?: boolean;
  /** Server-verified token holder — gold name + ✦ badge. */
  isHolder?: boolean;
}

/**
 * The shared player-avatar visual — the skin-colored capsule, the floating name
 * label, and the talking-face billboard. Lives here (not inline in
 * <EcctrlAvatar>) so the king-on-throne can render the EXACT same avatar as the
 * player it represents, just crowned. Geometry/material/billboards must match
 * the local capsule in <ControlsEcctrl> so every body in the scene is one system.
 */
export function PlayerAvatarBody({
  colorHex,
  colorIdentity,
  displayName,
  identity,
  showName = true,
  isHolder = false,
}: PlayerAvatarBodyProps): JSX.Element {
  const resolvedColor = resolveSkinColor(colorHex ?? "", colorIdentity ?? identity ?? "");
  return (
    <>
      <mesh castShadow>
        <capsuleGeometry args={[0.3, 0.7, 4, 12]} />
        <meshStandardMaterial {...avatarMaterialProps(resolvedColor)} />
      </mesh>
      {showName && displayName ? (
        <Billboard position={[0, 1.2, 0]}>
          <Text
            fontSize={0.25}
            color={isHolder ? "#f4d36a" : "#e8e8ef"}
            anchorX="center"
            anchorY="middle"
            outlineWidth={0.015}
            outlineColor="#0a0a10"
          >
            {isHolder ? `✦ ${displayName}` : displayName}
          </Text>
        </Billboard>
      ) : null}
      {identity ? <FaceBillboard identity={identity} y={1.8} /> : null}
    </>
  );
}

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
